import React, { createContext, useContext, useEffect, useState } from 'react';
import { CommonDialog, DialogDataProps } from '../components/app/CommonDialog';

export interface OpenDialogProps {
  children: React.ReactNode;
  customstyle?: Record<'paper', string>;
  id: string;
  keepMounted?: boolean;
  open?: boolean;
  onDismiss?: () => void;
  onConfirm?: (value?: boolean) => Promise<boolean>;
  title?: DialogDataProps;
  btnCancel?: DialogDataProps;
  btnOk?: DialogDataProps;
  maxWidth?: false | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
}

export interface DialogContextProps {
  openDialog: (options: OpenDialogProps) => void;
  closeDialog: () => void;
}

export const DialogContext = createContext<DialogContextProps | null>(null);

export const DialogProvider = ({ children }: { children: React.ReactNode }) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [dialogConfig, setDialogConfig] = useState<any>(null);

  const openDialog = (options: OpenDialogProps) => {
    setDialogConfig({
      children: options.children,
      customstyle: options?.customstyle,
      id: options.id,
      keepMounted: options?.keepMounted ?? true,
      open: true,
      onConfirm: options?.onConfirm,
      onDismiss: options?.onDismiss,
      title: options?.title ? options.title : { show: false, text: '' },
      btnCancel: options?.btnCancel
        ? options.btnCancel
        : { show: true, text: 'CANCEL' },
      btnOk: options?.btnOk ? options.btnOk : { show: true, text: 'OK' },
      maxWidth: options?.maxWidth ? options.maxWidth : 'xs',
    });
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setDialogConfig(null);
  };

  const onConfirm = async (): Promise<boolean> => {
    closeDialog();
    if (dialogConfig?.onConfirm) {
      return await dialogConfig?.onConfirm();
    }
    return true;
  };

  const onDismiss = () => {
    closeDialog();
    if (dialogConfig?.onDismiss) {
      dialogConfig.onDismiss();
    }
  };

  useEffect(() => {
    setLoading(false);
  }, []);

  if (loading) {
    return null;
  }

  return (
    <DialogContext.Provider value={{ openDialog, closeDialog }}>
      {children}
      {dialogConfig && (
        <CommonDialog
          customstyle={dialogConfig.customstyle}
          maxWidth={dialogConfig.maxWidth}
          id={dialogConfig.id}
          data-testid={dialogConfig.id}
          keepMounted={dialogConfig.keepMounted}
          open={dialogOpen}
          onDismiss={onDismiss}
          onConfirm={onConfirm}
          title={dialogConfig.title}
          btnCancel={dialogConfig.btnCancel}
          btnOk={dialogConfig.btnOk}
        >
          {dialogConfig?.children}
        </CommonDialog>
      )}
    </DialogContext.Provider>
  );
};

export function useDialog(): DialogContextProps {
  const context = useContext(DialogContext);
  return context || assertNever();
}

function assertNever(): never {
  throw Error('Cannot use useDialog outside of DialogProvider');
}
